import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api, type UserActivity } from '../lib/api'
import { useBidProfile } from '../lib/bid-profile'
import { PeoplePicker } from './PeoplePicker'

/** An ISO stamp in the reader's own locale, or '' when there is nothing/nonsense to show. */
function formatDate(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString()
}

/**
 * Settings → **User activity**. An admin picks a colleague and sees three facts about them: which
 * Niobe Desktop build they last used, and how many `/v1` calls they made over the last 7 and 30
 * days.
 *
 * The question it answers is a rollout question — "who is actually using Niobe, and is anyone
 * still on an old installer?" — which nothing in the product could answer before: the access log
 * recorded who called and when, never from WHICH client, because the Bearer is identical in the
 * browser and inside Niobe Desktop. Hence the `X-Niobe-Client` / `X-Niobe-Client-Version` headers
 * (lib/desktop.ts#clientHeaders) and the columns behind them (backend migration 015).
 *
 * TENANT ADMINS only, gated on `isTenantAdmin` DIRECTLY — never behind `isResolved`, which is
 * false whenever the bid is null and this page has none, so gating on it would hide the section
 * from everybody. Like every other conditional section here the client gate is an AFFORDANCE: the
 * backend refuses `GET /v1/admin/user-activity` for a non-admin before it queries anything, which
 * matters more than usual here — the payload is somebody else's usage data.
 *
 * Nothing is fetched until a person is chosen (`enabled`), so the always-mounted settings page
 * costs one request per deliberate lookup and none at all on load.
 */
export function UserActivitySection() {
  const { t } = useLang()
  const { isTenantAdmin } = useBidProfile(null)
  const [email, setEmail] = useState('')
  const [who, setWho] = useState('')

  const activityQ = useQuery<UserActivity>({
    queryKey: ['user-activity', email],
    queryFn: () => api.getUserActivity(email),
    enabled: isTenantAdmin && email !== '',
  })

  if (!isTenantAdmin) return null

  const data = activityQ.data
  const desktopVersion = data?.desktop
    ? (data.desktop.version ?? t('ua_desktop_unknown'))
    : t('ua_desktop_none')
  // Deliberately the DESKTOP stamp beside the version, not the overall one: "v0.7.25, last seen
  // today" would be a lie for someone who has since moved to the browser.
  const desktopSeen = formatDate(data?.desktop?.lastSeenAt ?? null)
  // A member with no rows at all is not a zero-usage member — the retention window (31 days) is
  // the floor under every figure here, so "never" is the honest word, not "0".
  const neverSeen = data != null && data.lastSeenAt === null

  return (
    <div className="settings-card ua-card">
      <p>{t('ua_intro')}</p>

      <div className="ua-picker">
        <PeoplePicker
          value={email}
          label={t('ua_pick_label')}
          onChange={(picked, displayName) => {
            setEmail(picked)
            setWho(displayName ?? picked)
          }}
        />
      </div>

      {/* The whole result is ONE live region, and it is always mounted. Focus stays in the picker
          across a lookup, so nothing here would ever be announced on arrival; and a region added
          at the same moment as its text is unreliably spoken. Only the content changes. */}
      <div
        className="ua-result"
        role="region"
        aria-live="polite"
        aria-label={t('ua_results_label').replace('{who}', who || email || '—')}
      >
        {!email ? (
          <p className="settings-note">{t('ua_empty')}</p>
        ) : activityQ.isLoading ? (
          <p className="settings-note">{t('ua_loading')}</p>
        ) : activityQ.isError ? (
          <p className="settings-note" role="alert">
            {t('ua_error')}
          </p>
        ) : data ? (
          <>
            <dl className="ua-facts">
              <div className="ua-fact">
                <dt>{t('ua_desktop_version')}</dt>
                <dd>
                  <span className="ua-fact-value">{desktopVersion}</span>
                  {desktopSeen && (
                    <span className="ua-fact-sub">
                      {t('ua_last_seen').replace('{when}', desktopSeen)}
                    </span>
                  )}
                </dd>
              </div>
              <div className="ua-fact">
                <dt>{t('ua_requests_7d')}</dt>
                <dd>
                  <span className="ua-fact-value">{data.requests7d}</span>
                </dd>
              </div>
              <div className="ua-fact">
                <dt>{t('ua_requests_30d')}</dt>
                <dd>
                  <span className="ua-fact-value">{data.requests30d}</span>
                </dd>
              </div>
            </dl>
            {neverSeen && <p className="settings-note">{t('ua_never')}</p>}
            <p className="settings-note ua-note">{t('ua_stale_note')}</p>
          </>
        ) : null}
      </div>
    </div>
  )
}
